import {
  forwardRef,
  useId,
  useState,
  type ChangeEvent,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/lib/utils/cn';

function valueLength(value: TextareaHTMLAttributes<HTMLTextAreaElement>['value']): number {
  if (value === undefined || value === null) return 0;
  return String(value).length;
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  /** Mostra contador de caracteres (usa maxLength) */
  showCounter?: boolean;
  containerClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    hint,
    error,
    id,
    value,
    defaultValue,
    maxLength,
    showCounter,
    className,
    containerClassName,
    onChange,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const taId = id ?? autoId;
  const hintId = hint ? `${taId}-hint` : undefined;
  const errorId = error ? `${taId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const [uncontrolledLength, setUncontrolledLength] = useState(() => valueLength(defaultValue));
  const isControlled = value !== undefined;
  const len = isControlled ? valueLength(value) : uncontrolledLength;

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    if (!isControlled) {
      setUncontrolledLength(event.currentTarget.value.length);
    }
    onChange?.(event);
  }

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      {label && (
        <div className="flex items-baseline justify-between">
          <label htmlFor={taId} className="text-[0.8125rem] font-medium text-ink">
            {label}
          </label>
          {showCounter && maxLength ? (
            <span
              className={cn(
                'font-mono text-[0.6875rem] tabular-nums',
                len > maxLength * 0.9 ? 'text-degraded' : 'text-muted',
              )}
            >
              {len}/{maxLength}
            </span>
          ) : null}
        </div>
      )}
      <textarea
        ref={ref}
        id={taId}
        value={value}
        defaultValue={defaultValue}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        onChange={handleChange}
        className={cn(
          'min-h-24 w-full resize-y rounded-md border bg-surface px-2.5 py-2 text-sm text-ink',
          'placeholder:text-muted',
          'transition-[border-color,box-shadow] duration-fast',
          'focus-visible:outline-none focus-visible:border-signal focus-visible:ring-2 focus-visible:ring-signal/15',
          error
            ? 'border-error focus-visible:border-error focus-visible:ring-error/20'
            : 'border-border hover:border-ink/20',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...rest}
      />
      {error ? (
        <p id={errorId} className="text-[0.6875rem] text-critical" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-[0.6875rem] text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
